import React, { useRef, useEffect } from 'react';
import { Rect, Circle, Text, Group, Transformer } from 'react-konva';
import { ELEMENT_COLORS, snapToGrid } from './elementDefaults';

function getColor(element) {
  if (element.type === 'table') {
    return ELEMENT_COLORS[element.tableType] || ELEMENT_COLORS.table;
  }
  if (element.type === 'zone') {
    return ELEMENT_COLORS[element.subType] || 'rgba(200,200,200,0.3)';
  }
  return ELEMENT_COLORS[element.subType] || '#999';
}

const CanvasElement = ({ element, isSelected, onSelect, onChange }) => {
  const shapeRef = useRef();
  const trRef = useRef();

  useEffect(() => {
    if (isSelected && trRef.current && shapeRef.current) {
      trRef.current.nodes([shapeRef.current]);
      trRef.current.getLayer().batchDraw();
    }
  }, [isSelected]);

  const handleDragEnd = (e) => {
    onChange({
      ...element,
      x: snapToGrid(e.target.x()),
      y: snapToGrid(e.target.y()),
    });
  };

  const handleTransformEnd = () => {
    const node = shapeRef.current;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    node.scaleX(1);
    node.scaleY(1);
    onChange({
      ...element,
      x: snapToGrid(node.x()),
      y: snapToGrid(node.y()),
      width: Math.max(20, snapToGrid(node.width() * scaleX)),
      height: Math.max(20, snapToGrid(node.height() * scaleY)),
      rotation: node.rotation(),
    });
  };

  const color = getColor(element);
  const isCircle = element.shape === 'circle';
  const cornerRadius = element.shape === 'rounded-rect' ? 10 : 0;
  const isZone = element.type === 'zone';

  const labelText = element.type === 'table'
    ? `T${element.tableNo}`
    : (element.label || element.subType);

  return (
    <>
      <Group
        x={element.x}
        y={element.y}
        draggable
        onClick={() => onSelect(element)}
        onTap={() => onSelect(element)}
        onDragEnd={handleDragEnd}
      >
        {isCircle ? (
          <Circle
            ref={shapeRef}
            x={element.width / 2}
            y={element.height / 2}
            radius={element.width / 2}
            fill={color}
            stroke={isSelected ? '#1976D2' : '#333'}
            strokeWidth={isSelected ? 2 : 1}
            opacity={isZone ? 0.4 : 0.9}
          />
        ) : (
          <Rect
            ref={shapeRef}
            width={element.width}
            height={element.height}
            fill={color}
            stroke={isSelected ? '#1976D2' : '#333'}
            strokeWidth={isSelected ? 2 : 1}
            cornerRadius={cornerRadius}
            opacity={isZone ? 0.4 : 0.9}
          />
        )}
        <Text
          text={labelText}
          x={0}
          y={isCircle ? element.height / 2 - 6 : element.height / 2 - 6}
          width={element.width}
          align="center"
          fontSize={element.width < 50 ? 8 : 11}
          fill={isZone ? '#333' : '#fff'}
          fontStyle="bold"
          listening={false}
        />
      </Group>
      {isSelected && (
        <Transformer
          ref={trRef}
          rotateEnabled={true}
          enabledAnchors={[
            'top-left', 'top-right', 'bottom-left', 'bottom-right',
            'middle-left', 'middle-right', 'top-center', 'bottom-center',
          ]}
          boundBoxFunc={(oldBox, newBox) => {
            if (newBox.width < 20 || newBox.height < 20) return oldBox;
            return newBox;
          }}
          onTransformEnd={handleTransformEnd}
        />
      )}
    </>
  );
};

export default CanvasElement;
